import {
  AddonTriggerContext,
  Avatar,
  FlexBox,
  InputRichText,
  SelectionList,
  SelectionListSelectedItem,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { SearchUser } from 'lib-shared-types-api';
import { useCallback, useContext, useEffect } from 'react';
import { Waypoint } from 'react-waypoint';

import { searchApi } from 'apis/search';
import {
  createMentionSpan,
  moveCaretAfterMention,
} from 'components/editorMentions/utils/mentions.utils';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getUserDisplayName } from 'utils/user';

import styles from './mentionsTrigger.module.scss';

export const MentionsTrigger = (): JSX.Element => {
  const {
    asyncStatus,
    items: searchUsers,
    changeArg,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
  });

  const editorContext = useContext(InputRichText.Context);
  const { matchText, insertContent } = useContext(AddonTriggerContext);

  const searchTerm = matchText.trim().slice(1);

  const handleSelectMention = useCallback(
    (selected: SelectionListSelectedItem<SearchUser>) => {
      const mention = createMentionSpan(selected.value);
      insertContent(mention.content, { inline: true });
      // Position the caret after the inserted mention when agent is selected with click/tap.
      moveCaretAfterMention(editorContext.ref.current, mention.id);

      ampli.mentionUserSelected();
    },
    [insertContent, editorContext],
  );

  useEffect(() => {
    if (searchTerm) {
      changeArg(
        {
          searchTerm,
          filter: {
            facets: {
              user_type: ['member'],
            },
          },
          offset: 0,
        },
        { debounceTime: 300 },
      );
    }
  }, [changeArg, searchTerm]);

  // Add role and aria-expanded to prevent close modals with Esc while trigger is open
  return (
    <FlexBox flexDirection="column" maxHeight="320px" role="menu" aria-expanded>
      <FlexBox
        alignItems="center"
        justifyContent="center"
        borderColor="neutral.200"
        borderWidth="0 0 100 0"
        padding="200"
        minWidth="280px"
      >
        <Text.Paragraph variant="4" subdued>
          {searchUsers.length > 0 && searchTerm
            ? `Matching @${searchTerm}`
            : 'Mention someone or press Esc to close'}
        </Text.Paragraph>
      </FlexBox>
      <SelectionList<SearchUser>
        className={styles.list}
        isActive
        onChange={(e) => handleSelectMention(e.selectedItem)}
      >
        {searchUsers.map((searchUser) => {
          const name = getUserDisplayName(searchUser);

          return (
            <SelectionList.Item
              key={searchUser.id}
              value={searchUser}
              contentClassName={styles.itemContent}
              noFixedSelectionStateSpace
            >
              <Avatar user={searchUser} />

              <div>
                <Text.Heading variant="heading-4">{name}</Text.Heading>

                {searchUser.email !== name && (
                  <Text.Paragraph variant="4" subdued>
                    {searchUser.email}
                  </Text.Paragraph>
                )}
              </div>
            </SelectionList.Item>
          );
        })}

        {asyncStatus === 'uninitialized' && (
          <FlexBox
            as="li"
            padding="200"
            justifyContent="center"
            alignItems="center"
          >
            <Text.Paragraph variant="4" subdued>
              Type agent name or email.
            </Text.Paragraph>
          </FlexBox>
        )}

        {asyncStatus === 'pending' && (
          <FlexBox
            as="li"
            padding="300"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner size={20} />
          </FlexBox>
        )}

        {asyncStatus === 'fulfilled' && !searchUsers.length && (
          <FlexBox as="li" padding="200" justifyContent="center">
            <Text.Paragraph variant="4" subdued>
              No agents found.
            </Text.Paragraph>
          </FlexBox>
        )}

        {asyncStatus === 'fulfilled' && hasNextPage && (
          <Waypoint onEnter={nextPage} />
        )}
      </SelectionList>
    </FlexBox>
  );
};
